import { computed, reactive, ref, watch } from "vue";
import { useStore } from "vuex";
import { EXPORT_FORMAT, type ExportFormatType } from "hoxton-shared/enums/exports";

import { type RootState } from "@/store/types";
import { CampaignAction } from "@/store/modules/campaign";

const DEFAULT_FT_CLICK_TAG_COUNT = "1";
const DEFAULT_FT_BROWSER_EXCLUSIONS = "ie8, ie9, ie10, ie11, opera";

function createCheckboxWithInput<T>(defaultValue: T) {
    const enabled = ref(!!defaultValue);
    const value = ref(defaultValue);

    watch(value, newValue => {
        if (newValue) {
            enabled.value = true;
        } else {
            enabled.value = false;
        }
    });

    return reactive({
        enabled,
        value
    });
}

const optimiseTextFiles = ref(false);
const optimiseFontFiles = ref(false);
const optimiseImages = ref(false);

const includeBackupImage = ref(false);

const ftClickTagCount = createCheckboxWithInput(DEFAULT_FT_CLICK_TAG_COUNT);
const ftBrowserExclusions = createCheckboxWithInput(DEFAULT_FT_BROWSER_EXCLUSIONS);

export function useDownloadHTMLZip() {
    const store = useStore<RootState>();

    const dcProfileId = computed({
        get: () => {
            return store.state.campaign.dcProfileId;
        },
        set: (value: number | undefined) => {
            void store.dispatch(CampaignAction.SetDcProfileId, value);
        }
    });

    const dcProfileIdError = computed(() => {
        if (!dcProfileId.value) {
            return "Required field";
        }

        if (!Number.isInteger(dcProfileId.value) || dcProfileId.value < 1) {
            return "Profile ID must be a number.";
        }

        return "";
    });

    const isButtonDisabled = computed<Partial<Record<ExportFormatType, boolean>>>(() => ({
        [EXPORT_FORMAT.DOUBLECLICK_SV]: dcProfileIdError.value !== ""
    }));

    function resetDownloadOptions() {
        optimiseTextFiles.value = false;
        optimiseFontFiles.value = false;
        optimiseImages.value = false;

        includeBackupImage.value = false;

        ftClickTagCount.enabled = true;
        ftClickTagCount.value = DEFAULT_FT_CLICK_TAG_COUNT;
        ftBrowserExclusions.enabled = true;
        ftBrowserExclusions.value = DEFAULT_FT_BROWSER_EXCLUSIONS;
    }

    function computeQueryParams(exportFormat: ExportFormatType) {
        return {
            ...getFlashTalkingParams(exportFormat),
            ...getOptimizationParams(exportFormat),
            ...getGenericParams(exportFormat),
            ...getDoubleClickParams(exportFormat)
        };
    }

    function getFlashTalkingParams(exportFormat: ExportFormatType) {
        if (exportFormat !== EXPORT_FORMAT.FLASHTALKING) {
            return {};
        }

        return {
            ...(ftClickTagCount.enabled && {
                clickTagCount: Number(ftClickTagCount.value)
            }),
            ...(ftBrowserExclusions.enabled && {
                browserExclusion: ftBrowserExclusions.value.split(",").map(s => s.trim())
            })
        };
    }

    function getOptimizationParams(exportFormat: ExportFormatType) {
        const optimizationFormats = [
            EXPORT_FORMAT.GENERIC,
            EXPORT_FORMAT.FLASHTALKING,
            EXPORT_FORMAT.DOUBLECLICK,
            EXPORT_FORMAT.DOUBLECLICK_SV,
            EXPORT_FORMAT.OPENDC
        ];

        if (!optimizationFormats.includes(exportFormat as (typeof optimizationFormats)[number])) {
            return {};
        }

        return {
            optimiseTextFiles: optimiseTextFiles.value,
            optimiseFontFiles: optimiseFontFiles.value,
            optimiseImages: optimiseImages.value
        };
    }

    function getGenericParams(exportFormat: ExportFormatType) {
        if (exportFormat !== EXPORT_FORMAT.GENERIC) {
            return {};
        }

        return {
            backupImage: includeBackupImage.value
        };
    }

    function getDoubleClickParams(exportFormat: ExportFormatType) {
        if (exportFormat !== EXPORT_FORMAT.DOUBLECLICK_SV) {
            return {};
        }

        return {
            dcProfileId: dcProfileId.value
        };
    }

    return {
        optimiseTextFiles,
        optimiseFontFiles,
        optimiseImages,
        includeBackupImage,
        ftClickTagCount,
        ftBrowserExclusions,
        dcProfileId,
        dcProfileIdError,

        // computed
        isButtonDisabled,

        // methods
        resetDownloadOptions,
        computeQueryParams
    };
}
